export enum OperationMode {
  ExecutiveMaster = 0,
  MDBMaster = 1,
  MDBSlave = 2,
  Pulse = 3,
  Recharger = 4
}

export enum LineOffset {
  None = 0,
  One = 1,
  AlwaysOne = -1
}

export interface VendingConfig {
  id: string | null;
  companyID: number;
  branchID: number;
  pinpadCode: string | null;
  TCOD: string;
  baseCoin: number;
  decimals: number;
  operationMode: OperationMode;
  products: VendingProduct[];
  mdbSlaveConfig: MDBSlaveConfig | null;
  pulseConfig: PulseConfig | null;
  appEventsEnabled?: null | boolean;
  topay: boolean;
  topayConfig: ToPayConfig | null;
  chargerConfig: ChargerConfig | null;
  /**
   * @deprecated Replaced by 'receiptConfig'
   */
  unattended: boolean;
  /**
   * @deprecated Replaced by 'receiptConfig'
   */
  unattendedConfig?: ReceiptConfig;
  configHash?: string;
  priceTableHash?: string;
  language?: string;
  receiptConfig?: ReceiptConfigV2 | null;
}

export interface VendingTerminalRequest extends VendingConfig {
  // These variables are sent by the website but shouldn't be sent
  // TODO: Remove them from PAYTEF WEB, adding them here to be striped
  topayMessages?: unknown;
  reset?: unknown;
}

export interface VendingTerminalResponse extends VendingConfig {
  topayMessages?: TopayMessageResponse[] | null;
}

export interface ReceiptConfig {
  ticketHeader: string;
  ticketFooter: string;
  ticketProduct: string;
}

export interface ReceiptConfigV2 {
  enabled: boolean;
  enableVirtualReceipt: boolean;
  printWithoutPrompt?: boolean;
  externalPrinter: ReceiptConfigV2ExternalPrinter;
  ticketHeader?: string | null;
  ticketFooter?: string | null;
  ticketProduct?: string | null;
}

export type PrinterType = "usbEscPos";
export type PrinterPaperWidth = 57 | 80;

export interface ReceiptConfigV2ExternalPrinter {
  enabled: boolean;
  printerType?: PrinterType | null;
  paperWidth?: PrinterPaperWidth | null;
  parameters?: string | null;
}

export interface ChargerConfig {
  sessionTimeout: number;
  creditCharge: boolean;
  paymentTimeout: number;
}

export interface VendingProduct {
  lineNumber: number;
  name: string;
  cardPrice: number;
  cashPrice: number;
  enabled: boolean;
  pulseCount?: number | null;
}

export type TopayQRType = "0" | "1" | "2" | "3" | null;
export type TopayQRCameraType = "frontal" | "back" | "camera_auto";
export type TopayReadType = "0" | "1" | "2" | "BlockBE" | null;
export type TopayCreditType = "0" | "1" | "2";

export interface ToPayConfig {
  machine: string;
  site: string;
  signingKey?: string;
  host?: string;
  /**
   * 0 -> topayQRInactive
   * 1 -> topayQRPromotions
   * 2 -> topayQRStandard
   * 3 -> topayQRIn
   */
  showQR?: TopayQRType;
  /**
   * 0 -> inactive
   * 1 -> standard
   * 2 -> inverted
   * BlockBE -> Lectura de bloque Banco España
   */
  read?: TopayReadType;
  /**
   * 0 -> balance
   * 1 -> free
   * 2 -> admin
   */
  visibleCredits?: TopayCreditType[];
  rechargePredefinedAmounts?: number[] | null;
  rechargeMinAmount?: number | null;
  rechargeEnableOtherAmount?: boolean | null;
  qrCameraType?: TopayQRCameraType;
  qrFlashOn?: boolean;
  ticketQr: boolean | null;
}

export type ProductSelectionPolicy = "firstProduct" | "userSelection";

export interface PulseConfig {
  ms: number;
  sessionTimeout: number;
  paymentTimeout: number;
  topayAutoSale: boolean;
  pulseInterval?: number | null;
  productSelectionPolicy?: ProductSelectionPolicy;
  enabledByInputSignal?: string | null;
}

export interface TopayMessageResponse {
  id: string;
  opType: string;
  card: string;
  timestamp: number;
  tcod: string;
  amount: number | null;
  auth: string | null;
  line: number | null;
  infoLine: number | null;
  reference: number | null;
  evaDtsDetails: string | null;
  companyID?: number | null;
  branchID?: number | null;
  terminalID?: string | null;
}

export type CashlessType = "cashless1" | "cashless2";

export interface MDBSlaveConfig {
  sessionTimeout: number; // Max time to wait for user product selection after sessión started
  vendTimeout: number; // Max delay to wait for Vend Success or Vend Failure
  lineOffset: LineOffset;
  creditCharge: boolean; // Si se puede cargar una medio de pago
  pricesInMachine: boolean; // Si se usan los precios propios o no
  validationDelay: boolean; // Si se debe retrasar la respuesta a un Vend Request
  alwaysIdle: boolean; // Quitar la necesidad de iniciar sesion para una venta
  defaultCredit: number; // Credito falso que se debe enviar a la maquina cuando se hacen ventas con tarjeta de credito
  alwaysCredit: boolean; // Enviar un mini saldo al iniciar sesion con saldo cero
  remoteVend: boolean;
  lineGrouping: boolean; // Si se deben agrupar lineas de mismo numero (requiere una lista de agrupaciones)
  cashless: CashlessType;
  paymentTimeout: number;
  initialPreauth: boolean;
  cashSaleIncludesBaseCoin: boolean;
  readerEnabledByDefault: boolean | null;
  usesSale: boolean | null;
  cardPaymentEnabled: boolean | null;
  updateMachinePrices?: boolean;
}

export type ExtraData = Record<string, number | string | boolean>;

/**
 * Esta clase es la que se envía desde la app de vending
 */
export interface AppEvent {
  category: string;
  tcod: string;
  timestamp: string;
  description?: string;
  level?: "R" | "E" | "W" | "I"; // REVIEW, ERROR, WARNING, INFO
  eventCount?: number | null;
  extraData?: ExtraData;
}

/**
 * Extensión de AppEvent para devolver en el servicio de consulta
 */
export interface StatisticData extends AppEvent {
  quantity: number;
}

export type CountByCategory = Record<string, number>;

export interface RemoteCommandRequest {
  tcod: string;
  name: string;
  user: string;
  branchID: number;
}

export interface ForceConfigPricesData {
  branchID: number;
  type: "prices" | "config";
  tcod: string;
}

export interface BranchLogoUrl {
  logoUrl: string;
  vendorLogoUrl: string;
}

export interface BranchLogoData {
  branchID: number;
  extension?: string;
  image?: string;
  action?: string;
  logoUrl?: boolean;
  vendorLogoUrl?: boolean;
}

export interface BranchLanguageData {
  language: string;
}

export interface BranchLanguageSave {
  branchID: number;
  language: string;
}

export interface BranchLogoDelete {
  branchID: number;
  action: string;
  logoUrl?: boolean;
  vendorLogoUrl?: boolean;
}

export interface TextValue<T> {
  text: string;
  value: T;
}

export interface VendingConfigOptions {
  operationModes: TextValue<OperationMode>[];
  cashlessTypes: TextValue<CashlessType>[];
  languages: TextValue<string>[];
  pulseMode: {
    inputSignalOptions: TextValue<string | null>[];
    productSelectionPolicies: TextValue<ProductSelectionPolicy>[];
  };
  lineOffsets: TextValue<LineOffset>[];
  baseCoinOptions: TextValue<number>[];
  topayOptions: {
    messageTypes: Record<string, string>;
    qrCameraTypes: TextValue<TopayQRCameraType>[];
    qrTypes: TextValue<TopayQRType>[];
    readTypes: TextValue<TopayReadType>[];
    topayUrls: TextValue<string>[];
    creditOptions: TextValue<TopayCreditType>[];
  };
  printerTypes: TextValue<PrinterType | null>[];
  printerPaperWidths: TextValue<PrinterPaperWidth | null>[];
  remoteCommandOptions: TextValue<string>[];
}
